import { dataVizGraphSchema } from "./schemas.js";
const trimText = (value) => value.trim();
const edgeIdFor = (source, target, label) => `${source}->${target}${label ? `:${label}` : ""}`;
export const normalizeDataVizGraph = (input, options = {}) => {
    const parsed = dataVizGraphSchema.parse(input);
    const dropDanglingEdges = options.dropDanglingEdges ?? true;
    const nodesById = new Map();
    for (const node of parsed.nodes) {
        const id = trimText(node.id);
        if (!id || nodesById.has(id)) {
            continue;
        }
        nodesById.set(id, {
            ...node,
            id,
            kind: trimText(node.kind) || "node",
            label: trimText(node.label) || id,
            detail: node.detail?.trim() || undefined,
            status: node.status?.trim() || "neutral",
        });
    }
    const edgeIds = new Set();
    const edges = parsed.edges.flatMap((edge) => {
        const source = trimText(edge.source);
        const target = trimText(edge.target);
        if (source === target) {
            return [];
        }
        if (dropDanglingEdges && (!nodesById.has(source) || !nodesById.has(target))) {
            return [];
        }
        const id = trimText(edge.id ?? edgeIdFor(source, target, edge.label?.trim()));
        if (!id || edgeIds.has(id)) {
            return [];
        }
        edgeIds.add(id);
        return [{
                ...edge,
                id,
                source,
                target,
                label: edge.label?.trim() || undefined,
                status: edge.status?.trim() || "neutral",
            }];
    });
    return {
        ...parsed,
        nodes: [...nodesById.values()],
        edges,
    };
};
const computeBounds = (positions) => {
    if (positions.length === 0) {
        return {
            min: [0, 0, 0],
            max: [0, 0, 0],
            center: [0, 0, 0],
        };
    }
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    positions.forEach((position) => {
        min[0] = Math.min(min[0], position[0]);
        min[1] = Math.min(min[1], position[1]);
        min[2] = Math.min(min[2], position[2]);
        max[0] = Math.max(max[0], position[0]);
        max[1] = Math.max(max[1], position[1]);
        max[2] = Math.max(max[2], position[2]);
    });
    return {
        min,
        max,
        center: [
            (min[0] + max[0]) / 2,
            (min[1] + max[1]) / 2,
            (min[2] + max[2]) / 2,
        ],
    };
};
const resolveRadialPosition = (index, total, options) => {
    const angle = (index / Math.max(total, 1)) * Math.PI * 2;
    const ring = 1 + (index % 3);
    const radius = options.radius + (ring - 1) * options.ringGap;
    return [
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.64,
        ((index % 5) - 2) * options.zGap,
    ];
};
const resolveGridPosition = (index, options) => {
    const columns = Math.max(options.gridColumns, 1);
    const column = index % columns;
    const row = Math.floor(index / columns);
    return [
        (column - (columns - 1) / 2) * options.gridGap,
        -row * options.gridGap,
        ((index % 3) - 1) * options.zGap,
    ];
};
export const resolveDataVizLayout = (input, options = {}) => {
    const graph = normalizeDataVizGraph(input);
    const strategy = options.strategy ?? "radial";
    const preserveProvidedPositions = options.preserveProvidedPositions ?? true;
    const resolvedOptions = {
        radius: options.radius ?? 120,
        ringGap: options.ringGap ?? 42,
        gridColumns: options.gridColumns ?? Math.ceil(Math.sqrt(Math.max(graph.nodes.length, 1))),
        gridGap: options.gridGap ?? 42,
        zGap: options.zGap ?? 18,
    };
    const nodePositions = {};
    const nodes = graph.nodes.map((node, index) => {
        const position = node.position && (strategy === "provided" || preserveProvidedPositions)
            ? [...node.position]
            : strategy === "grid"
                ? resolveGridPosition(index, resolvedOptions)
                : resolveRadialPosition(index, graph.nodes.length, resolvedOptions);
        nodePositions[node.id] = position;
        return {
            ...node,
            position,
        };
    });
    const positions = Object.values(nodePositions);
    const layout = {
        strategy,
        nodePositions,
        bounds: computeBounds(positions),
        nodeCount: nodes.length,
        edgeCount: graph.edges.length,
    };
    return {
        graph: {
            ...graph,
            nodes,
        },
        layout,
    };
};
export const summarizeDataVizGraph = (graph) => {
    const normalized = normalizeDataVizGraph(graph);
    const kinds = new Map();
    const statuses = new Map();
    normalized.nodes.forEach((node) => {
        kinds.set(node.kind, (kinds.get(node.kind) ?? 0) + 1);
        statuses.set(node.status ?? "neutral", (statuses.get(node.status ?? "neutral") ?? 0) + 1);
    });
    return {
        nodeCount: normalized.nodes.length,
        edgeCount: normalized.edges.length,
        kinds: Object.fromEntries([...kinds.entries()].sort()),
        statuses: Object.fromEntries([...statuses.entries()].sort()),
    };
};
//# sourceMappingURL=layout.js.map